import i18next from 'i18next'
import { VariableType } from '../../../../../../components/Table/types'
import { CustomReportVariable } from '../../../../../../redux/context/customReports/typesVariable'
import { CustomReportCategory } from '../../../../../../redux/context/customReports/typesCategory'
import {
  DisplayScale,
  NumberFormatConfig,
  resolveAutoScale,
  getScaleDivisor,
  getScaleSuffix
} from '../../../../../../shared/utils/numberScale'
import { getCategoryVariableType } from '../../utils'

const currencySymbolCache = new Map<string, { symbol: string; position: 'prefix' | 'suffix' }>()

const resolveCurrencySymbol = (currency: string): { symbol: string; position: 'prefix' | 'suffix' } => {
  const cached = currencySymbolCache.get(currency)
  if (cached) return cached
  const parts = new Intl.NumberFormat(i18next.language, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol'
  }).formatToParts(1)
  const symbolPart = parts.find(p => p.type === 'currency')
  const symbolIndex = parts.findIndex(p => p.type === 'currency')
  const integerIndex = parts.findIndex(p => p.type === 'integer')
  const result = {
    symbol: symbolPart?.value ?? currency,
    position: (symbolIndex < integerIndex ? 'prefix' : 'suffix') as 'prefix' | 'suffix'
  }
  currencySymbolCache.set(currency, result)
  return result
}

export interface ResolvedFormat {
  numberFormat: NumberFormatConfig
  variableType?: VariableType
  currency?: string
}

/**
 * Builds the default `NumberFormatConfig` for a given variable type.
 *  - percentage & ratio → 1 decimal
 *  - absolute (and everything else) → 0 decimals
 *  - displayScale defaults to `default` (no scaling)
 */
const getDefaultNumberFormat = (type: VariableType | undefined): NumberFormatConfig => ({
  decimals: type === VariableType.percentage || type === VariableType.ratio ? 1 : 0,
  displayScale: DisplayScale.default
})

/**
 * Single source of truth for resolving a custom report cell's formatting from a
 * (variable, category) pair. Used by the ag-grid value formatter/renderers,
 * chart point formatters, and Excel export so that they always agree.
 *
 * Resolution rules:
 *  - Category function override (growth/commonSize/percentage) wins on type
 *  - Otherwise variable.type
 *  - Defaults come from `getDefaultNumberFormat`; the variable's saved
 *    `style.numberFormat` is spread on top so stored values override defaults
 */
export const resolveFormat = (
  variable: Pick<CustomReportVariable, 'type' | 'style'> | undefined,
  category: CustomReportCategory | undefined,
  currency?: string
): ResolvedFormat => {
  const type = (category && getCategoryVariableType(category)) || variable?.type
  const defaults = getDefaultNumberFormat(type)
  const stored = variable?.style?.numberFormat
  return {
    numberFormat: {
      decimals: stored?.decimals ?? defaults.decimals,
      displayScale: stored?.displayScale ?? defaults.displayScale,
      showScaleSuffix: stored?.showScaleSuffix
    },
    variableType: type,
    currency
  }
}

/**
 * Pure formatter. Decimals/displayScale come entirely from `numberFormat`,
 * independent of legacy `formatValueByType` defaults, so new formatting rules
 * can evolve without touching shared helpers.
 */
export const formatVariableValue = (
  value: string | number | null | undefined,
  numberFormat: NumberFormatConfig,
  variableType: VariableType | undefined,
  currency?: string
): string => {
  if (!value) return ''
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  if (typeof numValue !== 'number' || Number.isNaN(numValue)) return ''

  const isPercentage = variableType === VariableType.percentage
  const isAbsolute = variableType === VariableType.absolute
  const { decimals = 0, displayScale, showScaleSuffix, showUnit } = numberFormat
  const effectiveScale = displayScale === DisplayScale.auto && !isPercentage ? resolveAutoScale(numValue) : displayScale
  const isScaled = !!effectiveScale && effectiveScale !== DisplayScale.default && !isPercentage
  const scaledValue = isScaled ? numValue / getScaleDivisor(effectiveScale) : numValue

  const intlOptions: Intl.NumberFormatOptions = {
    style: isPercentage ? 'percent' : 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }
  const formatted = scaledValue.toLocaleString(i18next.language, intlOptions).replace('−', '-')
  const suffix = showScaleSuffix !== false ? getScaleSuffix(effectiveScale) : ''
  const withScale = isScaled ? `${formatted}${suffix}` : formatted

  if (currency && isAbsolute && showUnit !== false) {
    const { symbol, position } = resolveCurrencySymbol(currency)
    return position === 'prefix' ? `${symbol}${withScale}` : `${withScale} ${symbol}`
  }
  return withScale
}

export const getFormattedValue = (
  value: unknown,
  variable: CustomReportVariable | undefined,
  category: CustomReportCategory | undefined
): string => {
  const { numberFormat, variableType, currency } = resolveFormat(variable, category)
  return formatVariableValue(value as string | number | null | undefined, numberFormat, variableType, currency)
}
